import {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useUserAuth } from "../../context/UserAuthContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  FaFilter,
  FaTimes,
  FaSearch,
  FaSortAmountDown,
  FaChevronDown,
  FaFire,
  FaCalendarWeek,
  FaChartBar,
  FaUsers,
  FaFolderOpen,
  FaLock,
  FaSkull,
  FaInbox,
  FaCrown,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import LeadCard from "./LeadCard";
import ReactSelect from "react-select";
const LEAD_STATUS_OPTIONS = [
  "Enquiry",
  "Follow Up",
  "Not Interested",
  "On-board",
  "Irrelevant",
  "Contacted",
  "Options Provided",
  "In Progress",
  "Completed",
  "Not converted",
  "No Response",
  "Meeting Fixed",
  "Meeting Done",
  "Visit",
  "Payment Received",
];

const LEAD_TYPE_OPTIONS = {
  wedding_quotation: "Wedding Quotation",
  birthday_quotation: "Birthday Quotation",
  anniversary_quotation: "Anniversary Quotation",
  corporate_quotations: "Corporate Quotation",
  destination_wedding_quotation: "Destination Wedding Quotation",
  international_wedding_quotation: "International Wedding Quotation",
  requests: "Requests",
  venue_bookings: "Venue Bookings",
  package_enquiry: "Package Enquiry",
};

const STATUS_OPTIONS = ["open", "close", "pending"];
const LABEL_OPTIONS = ["hot", "warm", "cold"];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "name_az", label: "Name A–Z" },
  { value: "name_za", label: "Name Z–A" },
  { value: "progress_high", label: "Progress: High → Low" },
  { value: "progress_low", label: "Progress: Low → High" },
];

// quick-filter stat cards
const STAT_FILTERS = [
  {
    key: "open",
    label: "New Leads",
    color: "#10b981",
    icon: <FaFolderOpen />,
    apiKey: "status",
    apiVal: "open",
  },
  {
    key: "hot",
    label: "Hot Leads",
    color: "#ef4444",
    icon: <FaFire />,
    apiKey: "label",
    apiVal: "hot",
  },
  {
    key: "converted",
    label: "Converted",
    color: "#d4a853",
    icon: <FaCrown />,
    apiKey: "stage_key",
    apiVal: "converted",
  },
  {
    key: "closed",
    label: "Closed",
    color: "#6366f1",
    icon: <FaLock />,
    apiKey: "status",
    apiVal: "close",
  },
  {
    key: "dead",
    label: "Dead",
    color: "#1a1a2e",
    icon: <FaSkull />,
    apiKey: "stage_key",
    apiVal: "not_converted",
  },
];

const FONT = "'DM Sans','Poppins','Segoe UI',sans-serif";

const C = {
  bg: "#f0f4f9",
  surface: "#ffffff",
  border: "#e8edf4",
  text: "#0f1c2e",
  muted: "#64748b",
  accent: "#2563EB",
  accentBg: "#EBF4FF",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  info: "#6366f1",
  gold: "#d4a853",
};

// ─── tiny components ──────────────────────────────────────────────────────────

const Chip = ({ label, onRemove, color = C.accent }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: `${color}15`,
      color,
      border: `1px solid ${color}30`,
      borderRadius: 20,
      fontSize: 11.5,
      fontWeight: 500,
      padding: "4px 12px 4px 14px",
      transition: "all 0.2s",
    }}
  >
    {label}
    <button
      onClick={onRemove}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        color,
        padding: 0,
        display: "flex",
        lineHeight: 1,
        opacity: 0.7,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
    >
      <FaTimes size={10} />
    </button>
  </span>
);

const Select = ({ value, onChange, placeholder, options, style = {} }) => (
  <div style={{ position: "relative", ...style }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        height: 40,
        appearance: "none",
        WebkitAppearance: "none",
        background: C.surface,
        color: value ? C.text : C.muted,
        border: `1.5px solid ${C.border}`,
        borderRadius: 12,
        padding: "0 36px 0 14px",
        fontSize: 13,
        fontFamily: FONT,
        cursor: "pointer",
        outline: "none",
        transition: "all 0.2s",
        fontWeight: 500,
      }}
      onFocus={(e) => {
        e.target.style.borderColor = C.accent;
        e.target.style.boxShadow = `0 0 0 3px ${C.accentBg}`;
      }}
      onBlur={(e) => {
        e.target.style.borderColor = C.border;
        e.target.style.boxShadow = "none";
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) =>
        typeof o === "string" ? (
          <option key={o} value={o}>
            {o}
          </option>
        ) : (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ),
      )}
    </select>
    <FaChevronDown
      size={10}
      style={{
        position: "absolute",
        right: 12,
        top: "50%",
        transform: "translateY(-50%)",
        color: C.muted,
        pointerEvents: "none",
      }}
    />
  </div>
);

const StatCard = ({ label, value, color, icon, isActive, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: isActive ? `${color}10` : C.surface,
      border: `1.5px solid ${isActive ? color : C.border}`,
      borderRadius: 18,
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      flex: 1,
      minWidth: 130,
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s cubic-bezier(0.25,0.8,0.25,1.4)",
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.boxShadow = `0 8px 24px ${color}20`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    <div
      style={{
        position: "absolute",
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: `${color}08`,
        filter: "blur(20px)",
      }}
    />
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 14,
        background: `linear-gradient(135deg,${color}20,${color}10)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ position: "relative", zIndex: 1 }}>
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: C.text,
          lineHeight: 1,
          letterSpacing: "-0.5px",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11.5,
          color: C.muted,
          marginTop: 4,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
    </div>
  </div>
);

// ─── pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  const { page, totalPages, total, limit } = pagination;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const pages = [];
  const delta = 2;
  for (
    let i = Math.max(1, page - delta);
    i <= Math.min(totalPages, page + delta);
    i++
  ) {
    pages.push(i);
  }

  const btnBase = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 10,
    border: `1.5px solid ${C.border}`,
    background: C.surface,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: FONT,
    transition: "all 0.2s",
    color: C.text,
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        marginTop: 32,
        padding: "16px 20px",
        background: C.surface,
        border: `1.5px solid ${C.border}`,
        borderRadius: 16,
      }}
    >
      <span style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>
        Showing{" "}
        <b style={{ color: C.text }}>
          {from}–{to}
        </b>{" "}
        of <b style={{ color: C.text }}>{total}</b> leads
      </span>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          style={{
            ...btnBase,
            opacity: page === 1 ? 0.4 : 1,
            cursor: page === 1 ? "not-allowed" : "pointer",
          }}
        >
          <FaArrowLeft size={12} />
        </button>

        {pages[0] > 1 && (
          <>
            <button onClick={() => onPageChange(1)} style={btnBase}>
              1
            </button>
            {pages[0] > 2 && (
              <span style={{ color: C.muted, padding: "0 4px" }}>…</span>
            )}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            style={{
              ...btnBase,
              background:
                p === page
                  ? "linear-gradient(135deg,#2563EB,#1d4ed8)"
                  : C.surface,
              color: p === page ? "#fff" : C.text,
              border: `1.5px solid ${p === page ? C.accent : C.border}`,
              boxShadow: p === page ? "0 4px 12px rgba(37,99,235,0.3)" : "none",
            }}
          >
            {p}
          </button>
        ))}

        {pages[pages.length - 1] < totalPages && (
          <>
            {pages[pages.length - 1] < totalPages - 1 && (
              <span style={{ color: C.muted, padding: "0 4px" }}>…</span>
            )}
            <button onClick={() => onPageChange(totalPages)} style={btnBase}>
              {totalPages}
            </button>
          </>
        )}

        <button
          disabled={page === totalPages}
          onClick={() => onPageChange(page + 1)}
          style={{
            ...btnBase,
            opacity: page === totalPages ? 0.4 : 1,
            cursor: page === totalPages ? "not-allowed" : "pointer",
          }}
        >
          <FaArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};

const LeadDashboard = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const { user, token } = useUserAuth();
  const manager_id = user?.uid

  const [searchParams, setSearchParams] = useSearchParams();

  const getParam = (key, def = "") => searchParams.get(key) || def;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);

  const [city, setCity] = useState([]);
  const [merchant, setMerchant] = useState([]);
  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [search, setSearch] = useState(getParam("search"));
  const [fStatus, setFStatus] = useState(getParam("status"));
  const [fLabel, setFLabel] = useState(getParam("label"));
  const [fLeadStatus, setFLeadStatus] = useState(getParam("lead_status"));
  const [fLeadType, setFLeadType] = useState(getParam("lead_type"));
  const [fDateFrom, setFDateFrom] = useState(getParam("date_from"));
  const [fDateTo, setFDateTo] = useState(getParam("date_to"));
  const [sortBy, setSortBy] = useState(getParam("sort", "newest"));
  const [page, setPage] = useState(parseInt(getParam("page", "1"), 10));
  const [activeStatFilter, setActiveStatFilter] = useState(
    getParam("stat_filter") || null,
  );

  const [form, setForm] = useState({
    cat_id: "",
    sub_cat_id: "",
  });

  const [fMerchantId, setFMerchantId] = useState(getParam("merchant_id"));

  const [fCityId, setFCityId] = useState(getParam("city_id"));

  const formHandler = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    hot: 0,
    converted: 0,
    closed: 0,
    dead: 0,
  });
  const limit = 20;

    const fetchVendors = async () => {
    try {
      const res = await axios.get(`${base_url}/api/v1/manager/vendors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMerchant(res.data.vendors);
    } catch (err) {
      console.log("Error fetching vendors:", err);
    }
  };

  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  useEffect(() => {
    const h = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setShowFilters(true);
    };
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const getCity = async () => {
    try {
      const res = await axios.get(
        `${base_url}/api/v1/cities/cache`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCity(res.data.data);
    } catch (err) {
      console.error("City error", err);
    }
  };

  useEffect(() => {
    fetchVendors()
    if (token) {
      getCity();
    }
  }, [token]);

  const buildParams = useCallback(
    (pg = 1) => {
      const p = {};

      if (debouncedSearch) p.search = debouncedSearch;

      if (fStatus) p.status = fStatus;
      if (fLabel) p.label = fLabel;
      if (fLeadStatus) p.lead_status = fLeadStatus;
      if (fLeadType) p.lead_type = fLeadType;

      if (fMerchantId) p.merchant_id = fMerchantId;
      if (fCityId) p.city_id = fCityId;

      if (fDateFrom) p.date_from = fDateFrom;
      if (fDateTo) p.date_to = fDateTo;

      if (sortBy !== "newest") p.sort = sortBy;
      if (pg > 1) p.page = pg;

      return p;
    },
    [
      debouncedSearch,
      fStatus,
      fLabel,
      fLeadStatus,
      fLeadType,
      fMerchantId,
      fCityId,
      fDateFrom,
      fDateTo,
      sortBy,
    ],
  );

  const fetchLeads = useCallback(
    async (pg = page) => {
      setLoading(true);
      const params = buildParams(pg);
      // sync URL
      setSearchParams(params, { replace: true });
      try {
        const endpoint = `${base_url}/api/v1/leads/manager`;
        const res = await axios.get(endpoint, {
          params: { ...params,manager_id, limit },
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("data", res.data);
        setData(res?.data?.data || []);
        setPagination(res?.data?.pagination || null);
        setStats(res?.data?.stats || {});
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    },
    [token, base_url, buildParams, page, limit, setSearchParams],
  );

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    fStatus,
    fLabel,
    fLeadStatus,
    fLeadType,
    fMerchantId,
    fCityId,
    fDateFrom,
    fDateTo,
    sortBy,
    activeStatFilter,
  ]);

  useEffect(() => {
    fetchLeads(page);
  }, [
    page,
    debouncedSearch,
    fStatus,
    fLabel,
    fLeadStatus,
    fLeadType,
    fMerchantId,
    fCityId,
    fDateFrom,
    fDateTo,
    sortBy,
    activeStatFilter,
  ]);

  const handlePageChange = (pg) => {
    setPage(pg);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // call / whatsapp helpers
  const makeCall = async (item) => {
    if (!item.mob_no) return;
    try {
      await axios.post(
        `${base_url}/api/v1/leads/create-call-log/`,
        {
          lead_id: item.id,
          merchant_id: item?.merchant_id,
          message: `Outbound Call : ${user?.name} to ${item?.lead_name}`,
          type: "call",
          caller_number: item.mob_no,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = `tel:${item?.mob_no}`;
    } catch (e) {
      alert("Something went wrong");
    }
  };

  const callLogInsert = async (item) => {
    if (!item?.whatsapp_no) return;
    try {
      await axios.post(
        `${base_url}/api/v1/leads/create-call-log`,
        {
          lead_id: item?.id,
          merchant_id: item?.merchant_id,
          message: `Whatsapp Chat initiated by ${user.name} to ${item?.lead_name}`,
          type: "whatsapp",
          caller_number: item.whatsapp_no,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.open(
        `https://wa.me/${item.whatsapp_no.replace(/\+/g, "")}`,
        "_blank",
      );
    } catch (e) {
      alert("Something went wrong");
    }
  };

  // active filter chips
  const chips = [
    fStatus && { label: fStatus, clear: () => setFStatus(""), color: C.info },
    fLabel && { label: fLabel, clear: () => setFLabel(""), color: C.warning },
    fLeadStatus && {
      label: fLeadStatus,
      clear: () => setFLeadStatus(""),
      color: "#ec4899",
    },
    fLeadType && {
      label: LEAD_TYPE_OPTIONS[fLeadType] || fLeadType,
      clear: () => setFLeadType(""),
      color: C.accent,
    },
    fDateFrom && {
      label: `From ${fDateFrom}`,
      clear: () => setFDateFrom(""),
      color: C.muted,
    },
    fDateTo && {
      label: `To ${fDateTo}`,
      clear: () => setFDateTo(""),
      color: C.muted,
    },
    activeStatFilter && {
      label: `Stat: ${activeStatFilter}`,
      clear: () => setActiveStatFilter(null),
      color: C.gold,
    },
  ].filter(Boolean);

  const clearAll = () => {
    setSearch("");
    setFStatus("");
    setFLabel("");
    setFLeadStatus("");
    setFLeadType("");
    setFDateFrom("");
    setFDateTo("");
    setSortBy("newest");
    setActiveStatFilter(null);
    setPage(1);
  };

  const activeCount = chips.length + (search ? 1 : 0);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <style>{`
          @keyframes spin    { to { transform: rotate(360deg); } }
          @keyframes fadeUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
          @keyframes slideIn { from { opacity:0; transform:translateX(-20px); } to { opacity:1; transform:none; } }
          @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.5; } }

          .filter-panel { animation: fadeUp 0.3s ease; }
          .stat-card    { animation: slideIn 0.4s ease both; }
          .stat-card:nth-child(1){animation-delay:.00s}
          .stat-card:nth-child(2){animation-delay:.05s}
          .stat-card:nth-child(3){animation-delay:.10s}
          .stat-card:nth-child(4){animation-delay:.15s}
          .stat-card:nth-child(5){animation-delay:.20s}
          input[type=date]::-webkit-calendar-picker-indicator{opacity:.5;cursor:pointer;transition:opacity .2s}
          input[type=date]::-webkit-calendar-picker-indicator:hover{opacity:1}
          select:focus,input:focus{outline:none}

          @media(max-width:768px){
            .filter-panel{
              position:fixed;top:0;left:0;right:0;bottom:0;
              z-index:1000;background:white;overflow-y:auto;
              border-radius:0;padding:20px;
            }
            .filter-overlay{
              position:fixed;top:0;left:0;right:0;bottom:0;
              background:rgba(0,0,0,.5);z-index:999;backdrop-filter:blur(2px);
            }
          }
        `}</style>

        <div
          style={{
            background: "linear-gradient(180deg,#f0f4f9,#f8fafc)",
            minHeight: "100vh",
            fontFamily: FONT,
            padding: isMobile ? "16px 12px 40px" : "28px 24px 48px",
          }}
        >
          <div style={{ maxWidth: 1340, margin: "0 auto" }}>
            {/* ── HEADER ──────────────────────────────────────────────────── */}
            <div style={{ marginBottom: 28 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 16,
                }}
              >
                <div>
                  <h1
                    style={{
                      fontSize: isMobile ? 24 : 30,
                      fontWeight: 800,
                      color: "#0f172a",
                      letterSpacing: "-0.5px",
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 14,
                        background: "linear-gradient(135deg,#2563EB,#1d4ed8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(37,99,235,.3)",
                      }}
                    >
                      <FaChartBar size={22} color="#fff" />
                    </div>
                    Lead Dashboard
                  </h1>
                  <p
                    style={{
                      color: C.muted,
                      fontSize: 14,
                      margin: "6px 0 0",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        background: C.accentBg,
                        color: C.accent,
                        fontWeight: 700,
                        padding: "2px 10px",
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    >
                      {pagination?.total ?? "—"}
                    </span>
                    leads
                    {activeStatFilter && (
                      <span
                        style={{
                          background: `${C.gold}15`,
                          color: C.gold,
                          fontWeight: 600,
                          padding: "2px 10px",
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      >
                        {activeStatFilter}
                      </span>
                    )}
                  </p>
                </div>

                {/* filter toggle */}
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px 22px",
                    borderRadius: 14,
                    border: `1.5px solid ${showFilters ? "#93c5fd" : C.border}`,
                    background: showFilters ? C.accentBg : C.surface,
                    color: showFilters ? C.accent : C.text,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                    fontFamily: FONT,
                    transition: "all 0.3s",
                    boxShadow: showFilters
                      ? "0 4px 12px rgba(37,99,235,.15)"
                      : "0 2px 8px rgba(0,0,0,.04)",
                  }}
                >
                  <FaFilter size={14} />
                  {!isMobile && "Filters"}
                  {activeCount > 0 && (
                    <span
                      style={{
                        background: C.accent,
                        color: "#fff",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {activeCount}
                    </span>
                  )}
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(auto-fit,minmax(${isMobile ? "140px" : "160px"},1fr))`,
                  gap: 12,
                  marginTop: 20,
                }}
              >
                {/* total — not clickable */}
                <div className="stat-card">
                  <StatCard
                    label="Total Leads"
                    value={stats.total}
                    color={C.accent}
                    icon={<FaUsers size={20} />}
                  />
                </div>
                {STAT_FILTERS.map((sf) => (
                  <div key={sf.key} className="stat-card">
                    <StatCard
                      label={sf.label}
                      value={stats[sf.key] || 0}
                      color={sf.color}
                      icon={sf.icon}
                      isActive={activeStatFilter === sf.key}
                      onClick={() =>
                        setActiveStatFilter(
                          activeStatFilter === sf.key ? null : sf.key,
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {isMobile && showFilters && (
              <div
                className="filter-overlay"
                onClick={() => setShowFilters(false)}
              />
            )}

            {/* ── FILTER PANEL ────────────────────────────────────────────── */}
            {showFilters && (
              <div
                className="filter-panel"
                style={{
                  background: C.surface,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: isMobile ? 0 : 20,
                  padding: "24px 24px 20px",
                  marginBottom: 24,
                  boxShadow: "0 8px 32px rgba(0,0,0,.06)",
                }}
              >
                {isMobile && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 20,
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 20,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <FaFilter size={18} style={{ color: C.accent }} /> Filters
                    </h3>
                    <button
                      onClick={() => setShowFilters(false)}
                      style={{
                        background: C.surface,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: 10,
                        width: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: C.text,
                        fontSize: 18,
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  {/* search */}
                  <div style={{ position: "relative", gridColumn: "span 2" }}>
                    <FaSearch
                      size={13}
                      style={{
                        position: "absolute",
                        left: 14,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: C.muted,
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search name, phone, city…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        width: "100%",
                        height: 40,
                        paddingLeft: 40,
                        paddingRight: 16,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: 12,
                        fontSize: 13.5,
                        fontFamily: FONT,
                        color: C.text,
                        background: C.surface,
                        boxSizing: "border-box",
                        transition: "all 0.2s",
                        fontWeight: 500,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = C.accent;
                        e.target.style.boxShadow = `0 0 0 3px ${C.accentBg}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = C.border;
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  <Select
                    value={fStatus}
                    onChange={setFStatus}
                    placeholder="Status"
                    options={STATUS_OPTIONS}
                  />
                  <Select
                    value={fLabel}
                    onChange={setFLabel}
                    placeholder="Lead label"
                    options={LABEL_OPTIONS}
                  />
                  <Select
                    value={fLeadStatus}
                    onChange={setFLeadStatus}
                    placeholder="Lead status"
                    options={LEAD_STATUS_OPTIONS}
                  />
                  <Select
                    value={fLeadType}
                    onChange={setFLeadType}
                    placeholder="Lead type"
                    options={Object.entries(LEAD_TYPE_OPTIONS).map(
                      ([value, label]) => ({ value, label }),
                    )}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
                    gap: 12,
                  }}
                >
                  {/* date from */}
                  <div>
                    <label
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginBottom: 5,
                      }}
                    >
                      <FaCalendarWeek size={10} style={{ marginRight: 5 }} />{" "}
                      From date
                    </label>
                    <input
                      type="date"
                      value={fDateFrom}
                      onChange={(e) => setFDateFrom(e.target.value)}
                      style={{
                        width: "100%",
                        height: 40,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: 12,
                        padding: "0 14px",
                        fontSize: 13.5,
                        fontFamily: FONT,
                        color: C.text,
                        background: C.surface,
                        boxSizing: "border-box",
                        transition: "all 0.2s",
                        fontWeight: 500,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = C.accent;
                        e.target.style.boxShadow = `0 0 0 3px ${C.accentBg}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = C.border;
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  {/* date to */}
                  <div>
                    <label
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginBottom: 5,
                      }}
                    >
                      <FaCalendarWeek size={10} style={{ marginRight: 5 }} /> To
                      date
                    </label>
                    <input
                      type="date"
                      value={fDateTo}
                      onChange={(e) => setFDateTo(e.target.value)}
                      style={{
                        width: "100%",
                        height: 40,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: 12,
                        padding: "0 14px",
                        fontSize: 13.5,
                        fontFamily: FONT,
                        color: C.text,
                        background: C.surface,
                        boxSizing: "border-box",
                        transition: "all 0.2s",
                        fontWeight: 500,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = C.accent;
                        e.target.style.boxShadow = `0 0 0 3px ${C.accentBg}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = C.border;
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  {/* sort */}
                  <div>
                    <label
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginBottom: 5,
                      }}
                    >
                      <FaSortAmountDown size={10} style={{ marginRight: 5 }} />{" "}
                      Sort by
                    </label>
                    <Select
                      value={sortBy}
                      onChange={setSortBy}
                      placeholder=""
                      options={SORT_OPTIONS}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
                    gap: 12,
                    marginTop: "20px",
                  }}
                >
                  <ReactSelect
                    placeholder="Search Merchant..."
                    options={merchant.map((m) => ({
                      value: m.id,
                      label: m.business_name,
                    }))}
                    value={
                      merchant
                        .map((m) => ({
                          value: m.id,
                          label: m.business_name,
                        }))
                        .find((m) => m.value == fMerchantId) || null
                    }
                    onChange={(selected) =>
                      setFMerchantId(selected ? selected.value : "")
                    }
                    isClearable
                    isSearchable
                  />

                  <ReactSelect
                    placeholder="Search City..."
                    options={city.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    value={
                      city
                        .map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))
                        .find((c) => c.value == fCityId) || null
                    }
                    onChange={(selected) =>
                      setFCityId(selected ? selected.value : "")
                    }
                    isClearable
                    isSearchable
                  />
                </div>

                {/* active chips */}
                {chips.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 16,
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Active filters:
                    </span>
                    {chips.map((c, i) => (
                      <Chip
                        key={i}
                        label={c.label}
                        onRemove={c.clear}
                        color={c.color}
                      />
                    ))}
                    <button
                      onClick={clearAll}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: C.danger,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "4px 10px",
                        fontFamily: FONT,
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        borderRadius: 8,
                        transition: "all .2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#fef2f2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "none";
                      }}
                    >
                      <FaTimes size={10} /> Clear all
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── LOADING ──────────────────────────────────────────────────── */}
            {loading && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "100px 0",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    border: "4px solid #e2e8f0",
                    borderTop: `4px solid ${C.accent}`,
                    animation: "spin .8s linear infinite",
                  }}
                />
                <span
                  style={{
                    color: C.muted,
                    fontSize: 15,
                    fontWeight: 500,
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                >
                  Loading leads…
                </span>
              </div>
            )}

            {/* ── EMPTY ────────────────────────────────────────────────────── */}
            {!loading && data.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "80px 20px",
                  background: C.surface,
                  borderRadius: 24,
                  border: `1.5px solid ${C.border}`,
                }}
              >
                <FaInbox
                  size={56}
                  style={{ color: C.muted, opacity: 0.5, marginBottom: 16 }}
                />
                <h4
                  style={{
                    color: C.text,
                    fontWeight: 700,
                    marginBottom: 8,
                    fontSize: 20,
                  }}
                >
                  No leads found
                </h4>
                <p
                  style={{
                    color: C.muted,
                    fontSize: 14,
                    maxWidth: 400,
                    margin: "0 auto",
                  }}
                >
                  {chips.length > 0 || search
                    ? "Try adjusting your filters to see more results."
                    : "Leads will appear here once assigned to you."}
                </p>
                {(chips.length > 0 || search) && (
                  <button
                    onClick={clearAll}
                    style={{
                      marginTop: 20,
                      padding: "12px 28px",
                      borderRadius: 14,
                      border: "none",
                      background: "linear-gradient(135deg,#2563EB,#1d4ed8)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      fontFamily: FONT,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      boxShadow: "0 4px 12px rgba(37,99,235,.3)",
                      transition: "all .3s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <FaTimes size={12} /> Clear all filters
                  </button>
                )}
              </div>
            )}

            {/* ── GRID ─────────────────────────────────────────────────────── */}
            {!loading && data.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(auto-fill,minmax(${isMobile ? "100%" : "320px"},1fr))`,
                  gap: 20,
                }}
              >
                {data?.map((item) => (
                  <LeadCard
                    key={item.id}
                    item={item}
                    onCall={makeCall}
                    onWhatsapp={callLogInsert}
                    onUpdate={() => fetchLeads(page)}
                  />
                ))}
              </div>
            )}

            {/* ── PAGINATION ───────────────────────────────────────────────── */}
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDashboard;
